/**
 * Data layer for the Title Profile streaming-economics charts.
 *
 * Brings d360's `RevenueOverTimeWidget` + `AcquisitionRetentionWidget` to this
 * app, rebuilt on our stack. The d360 widgets use `@parrot/parrot-ux` chart
 * primitives (Graph/Axis/Bars/Lines) which this app doesn't ship — so the charts
 * are rebuilt in Recharts and the data is derived here from the title endpoints.
 *
 * Verified wire shapes (plexie-labs-monorepo streaming_economics backend):
 *   GET /titles/:id/revenue           → TitleRevenueRow[]   (bare array)
 *   GET /titles/:id/revenue/time-series → TimeSeriesRow[]   (bare array)
 *   GET /titles/:id/rankings          → RankingRow[]        (bare array)
 *   GET /leaderboard?...              → { data: LeaderboardRow[], meta }
 *
 * See context-docs/TITLE_PROFILE_PARITY_SPEC.md.
 */
import { apiUrl, getJson } from './api';
import { prettyQuarter } from './format';

/* ── Wire types ──────────────────────────────────────────────────────────── */

/** Row from GET /titles/:id/revenue — one per (quarter, region, platform). */
export interface TitleRevenueRow {
  parrot_id: string;
  title: string;
  content_type: string;
  release_year: number;
  main_genre: string;
  quarter_end_date: string;
  region: string;
  availability_platform: string;
  revenue_contribution_usd: number;
  revenue_share: number;
  acquired_usd: number;
  acquisition_pct: number;
  retained_usd: number;
  retention_pct: number;
  subscribers_acquired: number;
  subscribers_retained: number;
}

/** Row from GET /titles/:id/revenue/time-series — pre-aggregated per quarter. */
export interface TimeSeriesRow {
  quarter_end_date: string;
  revenue_contribution_usd: number;
  acquired_usd: number;
  retained_usd: number;
}

/** Row from GET /titles/:id/rankings — this title's own rank per partition. */
export interface RankingRow {
  quarter_end_date: string;
  availability_platform: string;
  region: string;
  title_rank: number;
  revenue_contribution_usd: number;
}

/** Row from GET /titles/:id/rank-window — this title's rank + neighbours. */
export interface RankWindowRow {
  parrot_id: string;
  title: string;
  availability_platform: string;
  revenue_contribution_usd: number;
  title_rank: number;
  content_type?: string;
}

export interface RankWindowResult {
  ownRank: number | null;
  rows: RankWindowRow[];
  scope: {
    platform: string | null;
    region: string[] | null;
    timePeriod: string;
    startDate: string | null;
    endDate: string | null;
  };
}

/* ── Fetchers ────────────────────────────────────────────────────────────── */

export function fetchTitleRevenue(id: string): Promise<TitleRevenueRow[]> {
  return getJson<TitleRevenueRow[]>(apiUrl(`/titles/${id}/revenue`));
}

export function fetchTitleTimeSeries(id: string): Promise<TimeSeriesRow[]> {
  return getJson<TimeSeriesRow[]>(apiUrl(`/titles/${id}/revenue/time-series`));
}

export function fetchTitleRankings(id: string): Promise<RankingRow[]> {
  return getJson<RankingRow[]>(apiUrl(`/titles/${id}/rankings`));
}

/**
 * Fetch a title's rank window from the dedicated rank-window endpoint.
 * Scope params must match the chart's current selectors so the rank shown
 * in the Revenue & Rank panel agrees with the leaderboard for the same filters.
 */
export function fetchRankWindow(
  id: string,
  params: {
    platform?: string;
    region?: string[];
    timePeriod?: 'all' | 'latest';
    startDate?: string;
    endDate?: string;
    window?: number;
  },
): Promise<RankWindowResult> {
  const q = new URLSearchParams();
  if (params.platform) q.set('platform', params.platform);
  if (params.region?.length) q.set('region', params.region.join(','));
  if (params.timePeriod) q.set('timePeriod', params.timePeriod);
  if (params.startDate) q.set('startDate', params.startDate);
  if (params.endDate) q.set('endDate', params.endDate);
  if (params.window) q.set('window', String(params.window));
  return getJson<RankWindowResult>(apiUrl(`/titles/${id}/rank-window?${q.toString()}`));
}

/**
 * @deprecated Use fetchRankWindow instead. Kept temporarily for other callers.
 */
export function fetchLeaderboardWindow(params: {
  platform?: string;
  region?: string;
  endDate?: string;
  cumulative?: boolean;
  limit: number;
  offset: number;
}): Promise<{ data: { parrot_id: string; title: string; availability_platform: string; revenue_contribution_usd: number; revenue_share: number; title_rank: number }[]; meta: { count: number; hasMore: boolean; offset: number; limit: number } }> {
  const q = new URLSearchParams();
  if (params.platform) q.set('platform', params.platform);
  if (params.region) q.set('region', params.region);
  if (params.cumulative) q.set('timePeriod', 'all');
  else if (params.endDate) q.set('endDate', params.endDate);
  q.set('sort', 'revenue_contribution_usd');
  q.set('dir', 'desc');
  q.set('limit', String(params.limit));
  q.set('offset', String(params.offset));
  return getJson(apiUrl(`/leaderboard?${q.toString()}`));
}

/* ── Colors (tokens only — never hardcode hex) ───────────────────────────── */

/**
 * Region → chart token. Matches the d360 ROT widget's region palette exactly:
 *   EMEA = orange, LATAM = magenta, UCAN = purple, APAC = amber.
 * Anything unmapped falls back deterministically across the --data-* set so two
 * unknown regions never collide to the same colour.
 */
const REGION_TOKEN: Record<string, string> = {
  EMEA: 'var(--spectrum-3)', // orange
  LATAM: 'var(--data-6)', // magenta/pink
  UCAN: 'var(--data-8)', // purple
  APAC: 'var(--spectrum-2)', // amber
  Global: 'var(--data-2)',
};

const DATA_SLOTS = [
  'var(--data-1)',
  'var(--data-2)',
  'var(--data-4)',
  'var(--data-5)',
  'var(--data-6)',
  'var(--data-8)',
  'var(--data-9)',
  'var(--data-7)',
  'var(--data-3)',
  'var(--data-10)',
] as const;

/** Stable positive hash so an unmapped series keeps its colour across renders. */
function hashIndex(key: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h % mod;
}

export function colorForRegion(region: string): string {
  return REGION_TOKEN[region] ?? DATA_SLOTS[hashIndex(region, DATA_SLOTS.length)];
}

/** Platforms aren't a fixed enum, so colour them deterministically by name. */
export function colorForPlatform(platform: string, orderIndex?: number): string {
  if (orderIndex !== undefined && orderIndex < DATA_SLOTS.length) return DATA_SLOTS[orderIndex];
  return DATA_SLOTS[hashIndex(platform, DATA_SLOTS.length)];
}

/** d360 convention: retention is purple, acquisition is cyan (see --metric-*). */
export const SHARE_COLORS = {
  retention: 'var(--metric-retained)',
  acquisition: 'var(--metric-acquired)',
} as const;

/* ── Derivations ─────────────────────────────────────────────────────────── */

export type Breakdown = 'region' | 'platform';
export type RevenueMetric = 'cumulative' | 'quarterly';

/** A Recharts-friendly row: { quarter, label, [seriesName]: number, … }. */
export interface StackedPoint {
  quarter: string;
  label: string;
  [series: string]: string | number;
}

export interface SeriesBuildOptions {
  breakdown: Breakdown;
  metric: RevenueMetric;
  /** Only include rows in these regions. Empty/undefined = all. */
  regions?: string[];
  /** Only include rows on these platforms. Empty/undefined = all. */
  platforms?: string[];
}

export interface StackedResult {
  points: StackedPoint[];
  series: string[];
  /** `${quarter}-${series}` keys with no underlying data → render hatched. */
  notAvailable: Set<string>;
}

/** Distinct quarter_end_date values, ascending. */
export function sortedQuarters(rows: { quarter_end_date: string }[]): string[] {
  return [...new Set(rows.map((r) => r.quarter_end_date))].sort((a, b) => a.localeCompare(b));
}

export function allRegions(rows: TitleRevenueRow[]): string[] {
  return orderedSeries(rows, 'region');
}

export function allPlatforms(rows: TitleRevenueRow[]): string[] {
  return orderedSeries(rows, 'availability_platform');
}

/**
 * Build stacked bar data: one bar per quarter, one stack segment per series
 * (region or platform). Cumulative = running sum per series across quarters.
 * Supports region/platform multi-select filtering.
 *
 * Note: the OG "aggregate" toggle only gates single-vs-multi SELECTION in the
 * dropdown — it never collapses series. Series are always per-region or
 * per-platform based on `breakdown`, limited to the selected scope.
 */
export function buildStackedSeries(rows: TitleRevenueRow[], opts: SeriesBuildOptions): StackedResult {
  const { breakdown, metric, regions, platforms } = opts;

  // Apply multi-select scope filters.
  const regionSet = regions && regions.length ? new Set(regions) : undefined;
  const platformSet = platforms && platforms.length ? new Set(platforms) : undefined;
  const scoped = rows.filter(
    (r) =>
      (!regionSet || regionSet.has(r.region)) &&
      (!platformSet || platformSet.has(r.availability_platform)),
  );

  const quarters = sortedQuarters(scoped);
  const key = breakdown === 'region' ? 'region' : 'availability_platform';
  const series = orderedSeries(scoped, key);

  // quarter -> series -> summed revenue; and which (quarter,series) have rows.
  const perQuarter = new Map<string, Map<string, number>>();
  const present = new Set<string>();
  for (const q of quarters) perQuarter.set(q, new Map());
  for (const r of scoped) {
    const bucket = perQuarter.get(r.quarter_end_date);
    if (!bucket) continue;
    const name = (r[key] as string) || 'Unknown';
    bucket.set(name, (bucket.get(name) ?? 0) + (r.revenue_contribution_usd || 0));
    present.add(`${r.quarter_end_date}-${name}`);
  }

  const running = new Map<string, number>();
  const notAvailable = new Set<string>();
  const points: StackedPoint[] = quarters.map((q) => {
    const bucket = perQuarter.get(q)!;
    const point: StackedPoint = { quarter: q, label: prettyQuarter(q) };
    for (const name of series) {
      const quarterValue = bucket.get(name) ?? 0;
      if (!present.has(`${q}-${name}`)) notAvailable.add(`${q}-${name}`);
      if (metric === 'cumulative') {
        const total = (running.get(name) ?? 0) + quarterValue;
        running.set(name, total);
        point[name] = total;
      } else {
        point[name] = quarterValue;
      }
    }
    return point;
  });

  return { points, series, notAvailable };
}

/** Series ordered by total revenue desc, so the biggest stacks sort stably. */
function orderedSeries(rows: TitleRevenueRow[], key: keyof TitleRevenueRow): string[] {
  const totals = new Map<string, number>();
  for (const r of rows) {
    const name = (r[key] as string) || 'Unknown';
    totals.set(name, (totals.get(name) ?? 0) + (r.revenue_contribution_usd || 0));
  }
  return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name);
}

/** Latest quarter present in the rows (max quarter_end_date). */
export function latestQuarter(rows: { quarter_end_date: string }[]): string {
  return rows.reduce((max, r) => (r.quarter_end_date > max ? r.quarter_end_date : max), '');
}

/** The platform/region contributing the most revenue (for Acq/Ret default scope). */
export function primaryScope(rows: TitleRevenueRow[]): { platform?: string; region?: string } {
  const platform = orderedSeries(rows, 'availability_platform')[0];
  const region = orderedSeries(rows, 'region')[0];
  return { platform, region };
}

/**
 * Acquisition vs retention share lines over time, scoped to one platform+region.
 * Returns one point per quarter with both values as **percentage points**
 * (the pipeline stores them as fractions, e.g. 0.0613 → 6.13), so consumers can
 * render with formatPct directly and the Y axis reads in %.
 */
export interface SharePoint {
  quarter: string;
  label: string;
  retention: number | null;
  acquisition: number | null;
  retainedSubs: number;
  acquiredSubs: number;
}

export function buildShareSeries(
  rows: TitleRevenueRow[],
  platform: string | undefined,
  region: string | undefined,
): SharePoint[] {
  const scoped = rows.filter(
    (r) =>
      (platform ? r.availability_platform === platform : true) &&
      (region ? r.region === region : true),
  );
  const quarters = sortedQuarters(scoped);
  return quarters.map((q) => {
    const inQuarter = scoped.filter((r) => r.quarter_end_date === q);
    // One platform+region pair is one row per quarter, but guard for dupes.
    const retention = avg(inQuarter.map((r) => r.retention_pct));
    const acquisition = avg(inQuarter.map((r) => r.acquisition_pct));
    return {
      quarter: q,
      label: prettyQuarter(q),
      // Fractions → percentage points for display.
      retention: retention === null ? null : retention * 100,
      acquisition: acquisition === null ? null : acquisition * 100,
      retainedSubs: sum(inQuarter.map((r) => r.subscribers_retained)),
      acquiredSubs: sum(inQuarter.map((r) => r.subscribers_acquired)),
    };
  });
}

function sum(xs: number[]): number {
  return xs.reduce((a, b) => a + (b || 0), 0);
}

function avg(xs: number[]): number | null {
  const valid = xs.filter((x) => x !== null && x !== undefined && !Number.isNaN(x));
  if (!valid.length) return null;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}
